// GENERATED FILE — do not edit. Source of truth: contracts/. Regenerate: node scripts/sync-contracts.mjs

import { api } from '../apiClient';

import { z } from 'zod';
// Runtime validation: every named type above has a matching `<Name>Schema` zod export — the
// exact schema the backend validates with (story 2-92), so forms can `.parse()`/`.safeParse()`
// against it instead of hand-rolling checks. `.refine()` cross-field rules are preserved
// verbatim; the refine() error MESSAGE is not — the server owns user-facing text.

export type DashboardWidgetKey = "kpiSummary" | "nextWork" | "recentReports" | "activationChecklist";
export const DashboardWidgetKeySchema = z.enum(["kpiSummary", "nextWork", "recentReports", "activationChecklist"] as const);

export type DashboardLayoutWidget = {
  key: DashboardWidgetKey;
  title: string;
  description: string;
  allowed: boolean;
  reason: string | null;
  pinned: boolean;
};
export const DashboardLayoutWidgetSchema = z.object({
  key: DashboardWidgetKeySchema,
  title: z.string(),
  description: z.string(),
  allowed: z.boolean(),
  reason: z.string().nullable(),
  pinned: z.boolean(),
}).strict();

export type DashboardLayoutResponse = {
  widgets: Array<DashboardLayoutWidget>;
};
export const DashboardLayoutResponseSchema = z.object({
  widgets: z.array(DashboardLayoutWidgetSchema),
}).strict();

export type DashboardLayoutWidgetInput = {
  key: DashboardWidgetKey;
  pinned: boolean;
};
export const DashboardLayoutWidgetInputSchema = z.object({
  key: DashboardWidgetKeySchema,
  pinned: z.boolean(),
}).strict();

export type UpdateDashboardLayoutBody = {
  widgets: Array<DashboardLayoutWidgetInput>;
};
export const UpdateDashboardLayoutBodySchema = z.object({
  widgets: z.array(DashboardLayoutWidgetInputSchema),
}).strict().refine((body) => {
      // `for...of`, not `.every()`/`.filter()` with an inline arrow callback
      // and not indexed `array[i]` access: the sync-contracts refine-guard
      // only recognises `const`/`let`/`var` locals, not a nested arrow's own
      // parameter list (see `contracts/README.md`'s own "what the guard
      // accepts" section) — and the emitted source is type-checked as real
      // frontend TS under `noUncheckedIndexedAccess`, where `array[i]` is
      // `T | undefined` even right after a length check. `for...of` avoids
      // both problems at once: no callback parameter, no indexed access.
      const keys = ['kpiSummary', 'nextWork', 'recentReports', 'activationChecklist'];
      if (body.widgets.length !== keys.length) return false;
      // `for...of` is the one loop shape both the refine-guard and frontend
      // strict-TS indexing accept here (see the comment above) — an array-
      // iteration callback or an indexed access would fail one or the other.
      // eslint-disable-next-line no-restricted-syntax
      for (const key of keys) {
        let count = 0;
        // eslint-disable-next-line no-restricted-syntax -- same reason as above.
        for (const widget of body.widgets) {
          if (widget.key === key) count += 1;
        }
        if (count !== 1) return false;
      }
      return true;
    });

export type GetDashboardLayoutResponse = DashboardLayoutResponse;

export async function getDashboardLayout(): Promise<GetDashboardLayoutResponse> {
  const response = await api.get<GetDashboardLayoutResponse>(`/dashboard-layout`);
  return response.data;
}

export type UpdateDashboardLayoutResponse = DashboardLayoutResponse;

export async function updateDashboardLayout(body: UpdateDashboardLayoutBody): Promise<UpdateDashboardLayoutResponse> {
  const response = await api.put<UpdateDashboardLayoutResponse>(`/dashboard-layout`, body);
  return response.data;
}

export const dashboardLayoutKeys = {
  all: ["dashboardLayout"] as const,
  lists: () => [...dashboardLayoutKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...dashboardLayoutKeys.lists(), ...(filters ? [filters] : [])] as const,
  details: () => [...dashboardLayoutKeys.all, 'detail'] as const,
  detail: (id: string) => [...dashboardLayoutKeys.details(), id] as const,
};
